import { useLookupUserIdsOrEmails } from 'react-scale/components/WebinarSessions/api';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { IUserDetail } from 'react-scale/components/WebinarSessions/types';
import {
  DataTable,
  Icon,
  Flex,
  Text,
  Badge,
  Link,
  IconButton,
  Tooltip,
} from '@scale/scaleui-radix';
import { faTrash, faCopy } from '@fortawesome/pro-regular-svg-icons';
import { isNil } from 'lodash';
import { toast } from 'react-toastify';

interface UserTableProps {
  userIds: string[];
  invitedUserIds: string[];
  setInvitedUserIds: (userIds: string[]) => void;
  label: string;
}

interface UserTableData {
  id: string;
  name?: string;
  email?: string;
  isLoading: boolean;
}

const COLUMN_HELPER = DataTable.createColumnHelper<UserTableData>();

export const UserTable = ({
  userIds,
  invitedUserIds,
  setInvitedUserIds,
  label,
}: UserTableProps) => {
  const [fetchedUserDetails, setFetchedUserDetails] = useState<
    Record<string, { name?: string; email: string }>
  >({});
  const userIdsToFetch = userIds.filter(id => !fetchedUserDetails[id]);
  const {
    data: users,
    isFetching: isFetchingUsers,
    isLoading: isLoadingUsers,
  } = useLookupUserIdsOrEmails({
    userIdsOrEmails: userIdsToFetch,
  });

  const handleDeleteUser = useCallback(
    (userId: string) => {
      setInvitedUserIds(invitedUserIds.filter(id => id !== userId));
    },
    [invitedUserIds, setInvitedUserIds],
  );

  useEffect(() => {
    if (users && Object.keys(users).length > 0) {
      const userDetailsById: Record<string, { name?: string; email: string }> = users.reduce(
        (acc, item: IUserDetail) => {
          acc[item.id] = { name: isNil(item.name) ? undefined : item.name, email: item.email };
          return acc;
        },
        {} as Record<string, { name?: string; email: string }>,
      );
      setFetchedUserDetails(prev => ({ ...prev, ...userDetailsById }));
    }
  }, [users]);

  const COLUMNS = useMemo(
    () => [
      COLUMN_HELPER.accessor('id', {
        id: 'id',
        header: function headerComponent() {
          return <DataTable.CellContentDefault>ID</DataTable.CellContentDefault>;
        },
        filterFn: 'includesStringSensitive',
        cell: function cellComponent(props: any) {
          const id = props.getValue();
          const isExplicitlyInvited = invitedUserIds.includes(id);
          const deleteButton = (
            <IconButton
              variant="ghost"
              color="neutral"
              onClick={() => handleDeleteUser(props.row.original.id)}
              disabled={!isExplicitlyInvited}
            >
              <Icon size="1" icon={faTrash} label="Delete User" />
            </IconButton>
          );

          return (
            <Flex
              direction="row"
              gap="2"
              justify="between"
              align="center"
              wrap="wrap"
              className="truncate"
            >
              <Badge>
                <Link
                  href={`/corp/genai-ops-hub/search/${id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                >
                  {id}
                </Link>
              </Badge>

              <Flex direction="row" gap="2">
                <IconButton
                  variant="ghost"
                  color="neutral"
                  onClick={() => {
                    navigator.clipboard.writeText(id);
                    toast(`Copied user ID (${id}) to clipboard!`, {
                      progressStyle: { background: 'green' },
                    });
                  }}
                >
                  <Icon size="1" icon={faCopy} label="Copy User ID" />
                </IconButton>
                {isExplicitlyInvited ? (
                  deleteButton
                ) : (
                  <Tooltip
                    content={'User invited through a worker team, qualification, squad or audience'}
                  >
                    {deleteButton}
                  </Tooltip>
                )}
              </Flex>
            </Flex>
          );
        },
      }),
      COLUMN_HELPER.accessor('name', {
        id: 'name',
        header: function headerComponent() {
          return <DataTable.CellContentDefault>Name</DataTable.CellContentDefault>;
        },
        filterFn: 'includesString',
        cell: function cellComponent(props: any) {
          const name = props.getValue();
          return (
            <Text size="2" color="neutral" className="truncate">
              {name ?? '-'}
            </Text>
          );
        },
      }),
      COLUMN_HELPER.accessor('email', {
        id: 'email',
        header: function headerComponent() {
          return <DataTable.CellContentDefault>Email</DataTable.CellContentDefault>;
        },
        filterFn: 'includesString',
        cell: function cellComponent(props: any) {
          const email = props.getValue();
          return (
            <Text size="2" color="neutral" className="truncate">
              {email ?? '-'}
            </Text>
          );
        },
      }),
    ],
    [handleDeleteUser, invitedUserIds],
  );

  const dataForTable: UserTableData[] = useMemo(
    () =>
      userIds.map(id => ({
        id,
        name: fetchedUserDetails[id]?.name,
        email: fetchedUserDetails[id]?.email,
        isLoading: userIdsToFetch.includes(id) && isFetchingUsers,
      })),
    [userIds, fetchedUserDetails, userIdsToFetch, isFetchingUsers],
  );

  return (
    <Flex direction="column" gap="2">
      <Text as="label" size="2" weight="medium">
        {label}
      </Text>
      <DataTable.Root
        columns={COLUMNS}
        data={dataForTable}
        loading={isLoadingUsers}
        enableSorting={true}
        enableFilters={true}
        initialState={{
          pagination: {
            pageSize: 5,
          },
        }}
        getRowId={row => row.id}
      >
        {({ table }) => (
          <>
            <DataTable.Table className="overflow-x-hidden">
              <DataTable.Header>
                {table.getHeaderGroups().map(headerGroup => (
                  <DataTable.HeaderGroup key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <DataTable.ColumnHeaderCell key={header.id} header={header}>
                        {header.column.getCanSort() ? (
                          <DataTable.ColumnHeaderSort header={header} />
                        ) : null}
                      </DataTable.ColumnHeaderCell>
                    ))}
                  </DataTable.HeaderGroup>
                ))}
              </DataTable.Header>
              <DataTable.Body>
                {table.getRowModel().rows.map(row => (
                  <DataTable.Row key={row.id} row={row}>
                    {row.getVisibleCells().map(cell => (
                      <DataTable.Cell key={cell.id} cell={cell} />
                    ))}
                  </DataTable.Row>
                ))}
              </DataTable.Body>
            </DataTable.Table>
            <DataTable.Pagination>
              <DataTable.PaginationPageNavigation />
              <DataTable.PaginationPageSize />
              <DataTable.PaginationTotal />
            </DataTable.Pagination>
          </>
        )}
      </DataTable.Root>
    </Flex>
  );
};
